// Client for the Shilled HTTP API (SPEC.md → "v1.1 — Mentioned's rules"). Same origin by default. There is no
// sign-in and no session: the connected wallet is the account and is sent where an endpoint needs it.
// Errors become ApiError with a plain message.

const BASE = (typeof window !== "undefined" && window.SHILLED_API_BASE) || "";

export class ApiError extends Error {
  constructor(message, status = 0, body = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.friendly = true;
  }
}

function defaultMessage(status) {
  if (status === 400) return "The server couldn't use that. Check it and try again.";
  if (status === 403) return "That isn't allowed.";
  if (status === 404) return "Not found.";
  if (status === 413) return "That file is too large.";
  if (status === 429) return "Too many requests. Wait a moment and try again.";
  if (status >= 500) return "The Shilled server had a problem. Try again in a moment.";
  return `The request failed (${status}).`;
}

async function request(method, path, body, { signal } = {}) {
  const opts = { method, credentials: "same-origin", headers: { accept: "application/json" }, signal };
  if (typeof FormData !== "undefined" && body instanceof FormData) opts.body = body;
  else if (body !== undefined) {
    opts.headers["content-type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  let res;
  try {
    res = await fetch(BASE + path, opts);
  } catch (e) {
    if (e && e.name === "AbortError") throw e;
    throw new ApiError("Can't reach the Shilled server. Check your connection and try again.", 0);
  }
  const text = await res.text();
  let data = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = null; }
  }
  if (!res.ok) {
    const msg = (data && (data.error || data.message || data.reason)) || defaultMessage(res.status);
    throw new ApiError(typeof msg === "string" ? msg : defaultMessage(res.status), res.status, data);
  }
  return data;
}

const enc = encodeURIComponent;

export const api = {
  config: () => request("GET", "/api/config"),
  stats: () => request("GET", "/api/stats"),
  paid: (limit = 20) => request("GET", `/api/paid?limit=${enc(limit)}`),
  payout: (tx) => request("GET", `/api/paid/${enc(tx)}`),
  coins: ({ sort = "new", q = "" } = {}, opts) => request("GET", `/api/coins?sort=${enc(sort)}${q ? `&q=${enc(q)}` : ""}`, undefined, opts),
  coin: (token) => request("GET", `/api/coins/${enc(token)}`),
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return request("POST", "/api/upload", fd);
  },
  /** The wallet's personal code (sh-xxxxxxxx) for the Post on X text. */
  code: (address) => request("GET", `/api/code?address=${enc(address)}`),
  /** → { status: "verified", post, epoch } or { status: "rejected", code, reason } */
  submitPost: (url, token, wallet) => request("POST", "/api/posts", { url, token, wallet }),
  rewards: (address) => request("GET", `/api/rewards/${enc(address)}`),
  /** Pays the wallet everything it earned (no signature: the money can only go to that wallet). */
  claim: (wallet) => request("POST", "/api/claim", { wallet }),
  /** Launch: the server builds the transactions, the wallet signs them, the server sends them. */
  launchPrepare: (body) => request("POST", "/api/launch/prepare", body),
  launchSubmit: (body) => request("POST", "/api/launch/submit", body),
  launchFinish: (body) => request("POST", "/api/launch/finish", body),
};
